import React, { useEffect, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  UncontrolledTooltip,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { useNavigate, useLocation } from "react-router-dom"
import dayjs from "dayjs"
import BootstrapTable from "react-bootstrap-table-next"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import { queryAuditLogsNew } from "helpers/backend_helper"
import CustomRangePicker from "components/DatePickers/RangePicker"
import {
  dateFormatter,
  dateSorter,
  defaultPageSizeOptions,
  downloadExcel,
  showToast,
  stringSorter,
} from "utils/general"
import { Table as AntdTable, InputNumber, Select, Input as AntdInput, AutoComplete } from "antd"
const { Search } = AntdInput
import { tableDefaultHeight } from "constants/layout"

const AuditLogs = props => {
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [ongoingSearch, setOngoingSearch] = useState(false)
  const [searchText] = useState("")
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "days"),
    dayjs(),
  ])

  const [usernames, setUsernames] = useState([]);
  const [options, setOptions] = useState([]);

  const [usernameFilter, setUsernameFilter] = useState("")
  const [eventsFilter, setEventsFilter] = useState([])
  const [targetFilter, setTargetFilter] = useState([])
  const [detailsFilter, setDetailsFilter] = useState("")
  const [maxItems, setMaxItems] = useState("500")
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [title] = useState(props.t("Audit logs"))
  const [searchTriggers, setSearchTriggers] = useState(0) // Delayed search

  const [selectionData, setSelectionData] = useState([])
  const [selectionColumns, setSelectionColumns] = useState([])
  const [expandedKeys, setExpandedKeys] = useState([])

  const [eventOptions] = useState([
    { label: "CREATE", value: "CREATE" },
    { label: "READ", value: "READ" },
    { label: "UPDATE", value: "UPDATE" },
    { label: "DELETE", value: "DELETE" },
    { label: "RESET", value: "RESET" },
    { label: "LOGIN", value: "LOGIN" },
    { label: "LOGOUT", value: "LOGOUT" },
  ])

  const [targetOptions] = useState([
    { label: props.t("ThingDescriptions.Thingworx"), value: "Thingworx" },
    {
      label: props.t("ThingDescriptions.Thingworx / Front"),
      value: "Thingworx / Front",
    },
    {
      label: props.t("ThingDescriptions.SuvantoObjects"),
      value: "SuvantoObjects",
    },
    { label: props.t("ThingDescriptions.Groups"), value: "Groups" },
    {
      label: props.t("ThingDescriptions.GroupSettings"),
      value: "GroupSettings",
    },
    { label: props.t("ThingDescriptions.Users"), value: "Users" },
    { label: props.t("ThingDescriptions.SuvantoUsers"), value: "SuvantoUsers" },
    {
      label: props.t("ThingDescriptions.SuvantoGateways"),
      value: "SuvantoGateways",
    },
    { label: props.t("ThingDescriptions.SuvantoNodes"), value: "SuvantoNodes" },
    { label: props.t("ThingDescriptions.VideoDevices"), value: "VideoDevices" },
    { label: props.t("ThingDescriptions.VideoRooms"), value: "VideoRooms" },
    { label: props.t("ThingDescriptions.Carephones"), value: "Carephones" },
    { label: props.t("ThingDescriptions.GpsTrackers"), value: "GpsTrackers" },
    {
      label: props.t("ThingDescriptions.SuvantoOrders"),
      value: "SuvantoOrders",
    },
    {
      label: props.t("ThingDescriptions.SuvantoAlarms"),
      value: "SuvantoAlarms",
    },
    {
      label: props.t("ThingDescriptions.SuvantoJournal"),
      value: "SuvantoJournal",
    },
    {
      label: props.t("ThingDescriptions.SuvantoObjectUserLinks"),
      value: "SuvantoObjectUserLinks",
    },
    {
      label: props.t("ThingDescriptions.SuvantoAdminObjectLinks"),
      value: "SuvantoAdminObjectLinks",
    },
    {
      label: props.t("ThingDescriptions.SuvantoActivityProfiles"),
      value: "SuvantoActivityProfiles",
    },
    {
      label: props.t("ThingDescriptions.ScheduledMessages"),
      value: "ScheduledMessages",
    },
    {
      label: props.t("ThingDescriptions.TrackerLocations, TrackerEvents"),
      value: "TrackerLocations, TrackerEvents",
    },
    {
      label: props.t("ThingDescriptions.TrackerLocations"),
      value: "TrackerLocations",
    },
    { label: props.t("ThingDescriptions.Pindoras"), value: "Pindoras" },
    { label: props.t("ThingDescriptions.DoseCans"), value: "DoseCans" },
    {
      label: props.t("ThingDescriptions.MeasuringDevices"),
      value: "MeasuringDevices",
    },
    {
      label: props.t("ThingDescriptions.SuvantoAlarmSetups"),
      value: "SuvantoAlarmSetups",
    },
    {
      label: props.t("ThingDescriptions.Appointments"),
      value: "Appointments",
    },
    {
      label: props.t("ThingDescriptions.GroupTags"),
      value: "GroupTags",
    },
    {
      label: "AC " + props.t("Event"),
      value: "AC_CalendarEvent",
    },
    {
      label: "AC " + props.t("Virtual room"),
      value: "AC_VMR",
    },
    {
      label: "AC " + props.t("Recordings"),
      value: "AC_Recording",
    },
    {
      label: "AC " + props.t("Group"),
      value: "AC_Group",
    },
    {
      label: props.t("Video app settings"),
      value: "VideoAppSettings",
    },
  ])

  const navigate = useNavigate()
  const location = useLocation()

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  // Set URL dynamically based on multiple filters
  useEffect(() => {
    const timeout = setTimeout(() => {
      const params = new URLSearchParams()

      // Get date range
      if (dateRange?.length === 2) {
        const [startDate, endDate] = dateRange
        params.set(
          "date",
          `${startDate.format("YYMMDDHHmm")}-${endDate.format("YYMMDDHHmm")}`
        )
      }

      if (eventsFilter && eventsFilter.length > 0)
        params.set("events", eventsFilter)
      if (targetFilter && targetFilter.length > 0)
        params.set("target", targetFilter)

      if (usernameFilter) params.set("username", usernameFilter)
      if (detailsFilter) params.set("details", detailsFilter)

      const queryString = params.toString() // Set the string for URL
      navigate(queryString ? `?${queryString}` : "?", { replace: true }) // Apply changes to URL
    }, 100)

    return () => clearTimeout(timeout)
  }, [eventsFilter, targetFilter, ongoingSearch, dateRange, navigate, props.t])

  // Extract multiple filters from the URL and set their states
  useEffect(() => {
    setURL()
    const params = new URLSearchParams(location.search)
    const dateRangeParam = params.get("date")
    const eventsFilterParam = params.get("events")
    const targetFilterParam = params.get("target")
    const usernameFilterParam = params.get("username")
    const detailsFilterParam = params.get("details")

    const timeout = setTimeout(() => {
      // Set date range
      if (dateRangeParam) {
        const [start, end] = dateRangeParam.split("-")
        setDateRange([dayjs(start, "YYMMDDHHmm"), dayjs(end, "YYMMDDHHmm")])
      }

      // Set one or multiple event filters
      if (eventsFilterParam) {
        const eventsFilterValues = decodeURIComponent(eventsFilterParam)
          .split(",")
          .map(value => value.trim())
        setEventsFilter(eventsFilterValues)
      }

      // Set one or multiple target filters
      if (targetFilterParam) {
        const targetFilterValues = decodeURIComponent(targetFilterParam)
          .split(",")
          .map(value => value.trim())
        setTargetFilter(targetFilterValues)
      }

      if (usernameFilterParam) {
        setUsernameFilter(decodeURIComponent(usernameFilterParam))
      }
      if (detailsFilterParam) {
        setDetailsFilter(decodeURIComponent(detailsFilterParam))
      }
    }, 500)

    return () => clearTimeout(timeout)
  }, [])

  const setURL = () => {
    const params = new URLSearchParams(location.search) // Get current URL params
    navigate(`?${params.toString()}`, { replace: false }) // Push a new entry to the history stack
  }

  useEffect(() => {
    getLogs()
  }, [dateRange, searchTriggers])

  // Get logs
  const getLogs = () => {
    if (!ongoingSearch && dateRange) {
      setOngoingSearch(true)
      let rows = !Number.isNaN(Number.parseFloat(maxItems)) ? Number.parseInt(maxItems, 10) : 500

      queryAuditLogsNew(
        abortRef.current,
        dateRange[0].valueOf(),
        dateRange[1].valueOf(),
        usernameFilter,
        eventsFilter,
        targetFilter,
        detailsFilter,
        rows,
      )
        .then(result => {
          //console.log("Logs: ", result)
          if (result.statusCode == 200) {
            let names = usernames.slice()
            for (const element of result.entries) {
              element.targetDescription = "ThingDescriptions." + element.target

              if (element.username && !names.includes(element.username)) {
                names.push(element.username)
              }
            }
            setUsernames(names)
            setLogs(result.entries)
          } else showToast(props.t("Error"), "error")
        })
        .catch(e => {
          console.log(e)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingSearch(false)
        })
    }
  }

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, logs])

  // Handle search function
  const handleSearch = () => {
    let result = logs.filter(
      data =>
        data.details?.toLowerCase().includes(searchText.toLowerCase()) ||
        data.username?.toLowerCase().includes(searchText.toLowerCase())
    )
    setFilteredLogs(result)
  }

  const columns = [
    {
      dataIndex: "timestamp",
      title: props.t("Time"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["timestamp"], b["timestamp"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "username",
      title: props.t("Username"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "username", sortOrder)
      },
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "event",
      title: props.t("Event"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "event", sortOrder)
      },
      render: eventFormatter,
      width: "130px",
      ellipsis: true,
    },
    {
      dataIndex: "target",
      title: props.t("Target"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "target", sortOrder)
      },
      render: targetFormatter,
      width: "150px",
      ellipsis: true,
    },
    {
      dataIndex: "details",
      title: props.t("Content"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "details", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "comments",
      title: props.t("Comments"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "comments", sortOrder)
      },
      width: "160px",
      ellipsis: true,
    },
  ]

  function linkFormatter(cell, row) {
    return (
      <a href={"/clients/" + cell} target="_blank" rel="noreferrer">
        {cell}
      </a>
    )
  }

  function targetFormatter(cell, row) {
    return <span>{props.t(row.targetDescription)}</span>
  }

  function eventFormatter(cell, row) {
    if (row.event == "CREATE") {
      return (
        <span className="text-success">
          <i className="mdi mdi-database-plus me-1" />
          {row.event}
        </span>
      )
    } else if (row.event == "VIEW" || row.event == "READ") {
      return (
        <span className="text-muted">
          <i className="mdi mdi-eye me-1" />
          {row.event}
        </span>
      )
    } else if (row.event == "LOGIN") {
      return (
        <span className="text-info">
          <i className="mdi mdi-login me-1" />
          {row.event}
        </span>
      )
    } else if (row.event == "LOGOUT") {
      return (
        <span className="text-tertiary">
          <i className="mdi mdi-logout me-1" />
          {row.event}
        </span>
      )
    } else if (row.event == "UPDATE") {
      return (
        <span className="text-primary">
          <i className="mdi mdi-database-edit me-1" />
          {row.event}
        </span>
      )
    } else if (row.event == "RESET") {
      return (
        <span className="text-danger">
          <i className="mdi mdi-database-edit me-1" />
          {row.event}
        </span>
      )
    } else if (row.event == "DELETE") {
      return (
        <span className="text-danger">
          <i className="mdi mdi-delete me-1" />
          {row.event}
        </span>
      )
    } else return <span>{row.event}</span>
  }

  const onDateSelection = value => {
    setDateRange(value)
  }

  // Event changed
  const eventChange = newSelection => {
    setEventsFilter(newSelection)
  }

  /*const selectVisible = visible => {
    if (!visible) getLogs()
  }*/

  // Target changed
  const targetChange = newSelection => {
    setTargetFilter(newSelection)
  }

  // Row expanded - format table
  const handleOnExpand = (row, isExpand, rowIndex, e) => {
    let cols = []
    let data = []
    if (isExpand) {
      try {
        let json = JSON.parse(row.details)
        data = json

        let counter = 0
        for (const element of data) {
          element.react_id = "" + counter++
        }

        for (let field in json[0]) {
          let fld = "" + field
          if (fld == "react_id") {
            cols.push({ dataField: fld, text: fld, sort: false, hidden: true })
          } else if (
            ((row.event == "VIEW" || row.event == "CREATE") &&
              row.target == "SuvantoObjects" &&
              fld == "key") ||
            fld == "suvantoKey"
          )
            cols.push({
              dataField: fld,
              text: fld,
              sort: true,
              formatter: linkFormatter,
            })
          else cols.push({ dataField: fld, text: fld, sort: true })
        }
      } catch (err) {
        console.log(err)
      }
    }

    setSelectionColumns(cols)
    setSelectionData(data)
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [
        props.t("Time"),
        props.t("Username"),
        props.t("Event"),
        props.t("Target"),
        props.t("Content"),
        props.t("Comments"),
      ],
    ]
    const data = filteredLogs.map(elt => [
      dateFormatter(elt.timestamp),
      elt.username,
      elt.event,
      elt.target,
      elt.details,
      elt.comments,
    ])

    downloadExcel(title, heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  // Expanded table row
  const expandedRowRender = record => {
    return (
      <>
        {selectionColumns && selectionColumns.length > 1 && (
          <>
            <ToolkitProvider
              keyField="react_id"
              columns={selectionColumns}
              data={selectionData}
            >
              {toolkitProps => (
                <React.Fragment>
                  <div
                    className="table-responsive"
                    style={{
                      maxWidth: "80vw",
                      backgroundColor: "white",
                    }}
                  >
                    <BootstrapTable
                      id="selection-table"
                      keyField={"react_id"}
                      responsive
                      classes={"table align-middle table-nowrap"}
                      headerWrapperClasses={"thead-light"}
                      {...toolkitProps.baseProps}
                    />
                  </div>
                </React.Fragment>
              )}
            </ToolkitProvider>
          </>
        )}
      </>
    )
  }

  const getUserSuggestions = (searchText) => {
    if (!searchText) return []
    let filteredUsers = usernames.filter(u => u.includes(searchText))
    let suggestions = []
    for (const element of filteredUsers) {
      suggestions.push({ value: element })
    }
    return suggestions
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title={props.t("Audit logs")}
          />
          <Card>
            <CardBody>
              <Row>
                <Col>
                  {logs != null && (
                    <React.Fragment>
                      <Row>
                        <Col className="mb-3">
                          <Select
                            mode={"multiple"}
                            disabled={ongoingSearch}
                            allowClear={true}
                            value={eventsFilter}
                            placeholder={props.t("Event")}
                            onChange={eventChange}
                            //onOpenChange={selectVisible}
                            //onClear={() => {getLogs(); setEventsFilter([])}}
                            options={eventOptions}
                            style={{ width: 100 + "%", minWidth: "240px" }}
                          />
                        </Col>
                        <Col className="mb-3">
                          <Select
                            mode={"multiple"}
                            disabled={ongoingSearch}
                            allowClear={true}
                            value={targetFilter}
                            placeholder={props.t("Target")}
                            onChange={targetChange}
                            //onOpenChange={selectVisible}
                            options={targetOptions}
                            style={{ width: 100 + "%", minWidth: "300px" }}
                          />
                        </Col>
                        <Col className="mb-3">
                          <AutoComplete
                            placeholder={props.t("Username")}
                            disabled={ongoingSearch}
                            value={usernameFilter}
                            style={{ minWidth: 180, width: "100%" }}
                            options={options}
                            defaultActiveFirstOption={false}
                            onChange={v => {
                              setUsernameFilter(v)
                            }}
                            onSearch={(text) => setOptions(getUserSuggestions(text))}
                          >
                            <Search
                              allowClear
                              onSearch={() => {
                                setSearchTriggers(prevValue => prevValue + 1)
                              }}
                            />
                          </AutoComplete>
                        </Col>
                        {/** 
                        <Col className="col-auto mb-3">
                          <Search
                            placeholder={props.t("Username")}
                            disabled={ongoingSearch}
                            allowClear
                            value={usernameFilter}
                            onChange={v => {
                              setUsernameFilter(v.target.value)
                            }}
                            onSearch={() => {
                              setSearchTriggers(prevValue => prevValue + 1)
                            }}
                            style={{ width: "200px" }}
                          />
                        </Col>*/}
                        <Col className="mb-3">
                          <Search
                            placeholder={props.t("Content")}
                            disabled={ongoingSearch}
                            allowClear
                            value={detailsFilter}
                            onChange={v => {
                              setDetailsFilter(v.target.value)
                            }}
                            onSearch={() => {
                              setSearchTriggers(prevValue => prevValue + 1)
                            }}
                            style={{ minWidth: "180px", width: "100%" }}
                          />
                        </Col>
                        {/** 
                        <Col className="col-auto mb-3">
                          <Search
                            placeholder={props.t("Search")}
                            allowClear
                            value={searchText}
                            onChange={event => {
                              setSearchText(event.target.value)
                            }}
                            style={{ width: "200px" }}
                          />
                        </Col>*/}
                        <Col className="col-auto mb-3">
                          <InputNumber
                            style={{ width: "135px" }}
                            suffix={props.t("max")}
                            disabled={ongoingSearch}
                            value={maxItems}
                            step={100}
                            onChange={newVal => {
                              setMaxItems(newVal)
                            }}
                            onPressEnter={() => {
                              getLogs()
                            }}
                          />
                        </Col>
                        <Col style={{ maxWidth: "450px" }}>
                          <div className="mb-3" style={{ minWidth: "350px" }}>
                            <CustomRangePicker
                              value={dateRange}
                              rangeChanged={onDateSelection}
                              showTime={true}
                              allowClear={false}
                            />
                          </div>
                        </Col>
                        <Col className="col-auto">
                          <Button
                            className="compact-button"
                            color="primary"
                            disabled={ongoingSearch}
                            onClick={() => {
                              getLogs()
                              setURL()
                            }}
                          >
                            <i className="mdi mdi-database-search me-2" />
                            {props.t("Search")}
                          </Button>
                          <Button
                            className="compact-button ms-3"
                            id="calendar-button"
                            color="primary"
                            disabled={ongoingSearch}
                            onClick={() => {
                              setDateRange([dateRange[0], dayjs()])
                            }}
                          >
                            <UncontrolledTooltip
                              placement="bottom"
                              target="calendar-button"
                            >
                              {props.t("Refresh time")}
                            </UncontrolledTooltip>
                            <i className="mdi mdi-calendar-refresh" />
                          </Button>
                          <Button
                            className="compact-button ms-3"
                            id="reset-button"
                            color="danger"
                            outline
                            disabled={ongoingSearch}
                            onClick={() => {
                              setDateRange([dayjs().subtract(1, "days"), dayjs()])
                              setUsernameFilter("")
                              setTargetFilter([])
                              setEventsFilter([])
                              setDetailsFilter("")
                              setMaxItems(500)
                            }}
                          >
                            <UncontrolledTooltip
                              placement="bottom"
                              target="reset-button"
                            >
                              {props.t("Clear filters")}
                            </UncontrolledTooltip>
                            <i className="mdi mdi-filter-remove" />
                          </Button>
                        </Col>
                        <Col>
                          <span className="float-end ms-2">
                            <UncontrolledTooltip
                              placement="bottom"
                              target="downloadstatstable"
                            >
                              {props.t("Download")}
                            </UncontrolledTooltip>
                            <Dropdown
                              id="printButtonServicesStats"
                              isOpen={menu}
                              toggle={() => setMenu(!menu)}
                              className="download-btn text-center"
                              type="button"
                            >
                              <DropdownToggle
                                id="downloadstatstable"
                                className="text-muted"
                                tag="i"
                              >
                                <i className="mdi mdi-download" />
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={() => generateExcel()}>
                                  {props.t("Download .xlsx")}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </span>
                          <div className="mt-2 float-end">
                            <p>
                              {filteredLogs.length} {props.t("results found")}
                            </p>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mt-1">
                        <Col xl="12">
                          <AntdTable
                            className="colored-header narrow-header"
                            bordered
                            size="small"
                            sticky
                            rowKey={"id"}
                            columns={columns}
                            dataSource={filteredLogs}
                            expandable={{
                              showExpandColumn: true,
                              expandRowByClick: true,
                              expandedRowKeys: expandedKeys,
                              onExpand: (expanded, record) => {
                                let keys = []
                                if (expanded) {
                                  keys.push(record.id)
                                  handleOnExpand(record, true, 0, null)
                                }
                                setExpandedKeys(keys)
                              },
                              rowExpandable: record =>
                                record.details?.length > 2,
                              expandedRowRender: expandedRowRender,
                            }}
                            pagination={{
                              showSizeChanger: true,
                              defaultPageSize: 50,
                              pageSizeOptions: defaultPageSizeOptions,
                            }}
                            loading={ongoingSearch}
                            tableLayout="auto"
                            scroll={{
                              x: "max-content",
                              y: tableDefaultHeight,
                            }}
                          />
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

AuditLogs.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(AuditLogs)
